import React, { useState, useEffect } from "react";
import axios from "axios";


import "react-phone-number-input/style.css"; // Ensure you have this import
import { BusinessUser } from "../../CRUD/CampSites/types";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { PhoneNumberUtil } from "google-libphonenumber";

interface BusinessUserFormProps {
  businessUserId?: number;
  onSuccess: () => void;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  onEdit?: (businessUser: BusinessUser, isEditing: boolean) => void;
  initialCompanyName?: string;
  initialName?: string;
  initialEmail?: string;
  initialPhoneNumber?: string;
  initialAddress?: string;
  initialAddress2?: string;
  initialCity?: string;
  initialState?: string;
  initialZipCode?: string;
  initialCountry?: string;
  initialComments?: string;
  isEditing?: boolean;
}

const localeMap: { [key: string]: string } = {
  "en-US": "US",
  "fr-FR": "FR",
  "sv-SE": "SE",
  sv: "SE", // Add short code for Swedish
  // Add more mappings as needed
};
const phoneUtil = PhoneNumberUtil.getInstance();
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Add email regex

const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    return false;
  }
};

const BusinessUserForm: React.FC<BusinessUserFormProps> = ({
  businessUserId,
  onSuccess,
  onSubmit,
  onCancel,
  onEdit,
  initialCompanyName = "",
  initialName = "",
  initialEmail = "",
  initialPhoneNumber = "",
  initialAddress = "",
  initialAddress2 = "",
  initialCity = "",
  initialZipCode = "",
  initialCountry = "",
  initialComments = "",
  isEditing = false,
}) => {
  const [companyName, setCompanyName] = useState<string>(initialCompanyName); // Add companyName state
  const [name, setName] = useState<string>(initialName);
  const [email, setEmail] = useState<string>(initialEmail);
  const [phoneNumber, setPhoneNumber] = useState<string>(initialPhoneNumber);
  const [address, setAddress] = useState<string>(initialAddress);
  const [address2, setAddress2] = useState<string>(initialAddress2);
  const [city, setCity] = useState<string>(initialCity);
  const [zipCode, setZipCode] = useState<string>(initialZipCode);
  const [country, setCountry] = useState<string>(initialCountry);
  const [comments, setComments] = useState<string>(initialComments);
  const [businessCode, setBusinessCode] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState<string>(""); // Add phone state

  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null); // Add email error state
  const [defaultCountry, setDefaultCountry] = useState<string>("US");
  const [phoneTouched, setPhoneTouched] = useState<boolean>(false); // Track if phone input has been touched

  useEffect(() => {
    const userLocale = navigator.language;
    const country = localeMap[userLocale] || "US";
    setDefaultCountry(country);
  }, []);

  useEffect(() => {
    if (businessUserId) {
      const fetchBusinessUser = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/BusinessUser/getBusinessUser/${businessUserId}`
          );
          const businessUser: BusinessUser = response.data as BusinessUser;
          const {
            companyName,
            name,
            email,
            phoneNumber,
            address,
            address2,
            city,
            zipCode,
            country,
            comments,
            businessCode,
          } = businessUser;
          setCompanyName(companyName ?? ""); // Ensure companyName is set correctly
          setName(name);
          setEmail(email);
          setPhoneNumber(phoneNumber);
          setPhone(initialPhoneNumber); // Ensure phoneNumber is set correctly
          setAddress(address);
          setAddress2(address2);
          setCity(city);
          setZipCode(zipCode);
          setCountry(country);
          setComments(comments);
          setBusinessCode(businessCode);
        } catch (error) {
          console.error("Error fetching business user details", error);
        }
      };

      fetchBusinessUser();
    }
  }, [businessUserId, initialPhoneNumber]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const businessUserDTO = {
      id: businessUserId,
      companyName,
      name,
      email,
      phoneNumber,
      address,
      address2,
      city,
      zipCode,
      country,
      comments,
      businessCode,
    };

    try {
      const token = localStorage.getItem("accessToken");
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      if (businessUserId) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/BusinessUser/updateBusinessUser`,
          businessUserDTO,
          config
        );
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/BusinessUser/addBusinessUser`,
          businessUserDTO,
          config
        );
        const responseData = response.data as { success: boolean; message: string };
        if (!responseData.success) {
          const responseData = response.data as { message: string };
          setError(responseData.message);
          return;
        }
      }

      // Clear the form state
      setCompanyName("");
      setName("");
      setEmail("");
      setPhoneNumber("");
      setPhone("");
      setAddress("");
      setAddress2("");
      setCity("");
      setZipCode("");
      setCountry("");
      setComments("");
      setBusinessCode("");
      setError(null);

      // Trigger the onSuccess callback to show the list
      onSuccess();
    } catch (error) {
      console.error("Error saving business user", error);
      setError("An error occurred while saving the business user.");
    }
  };

  const handlePhoneNumberChange = (value?: string) => {
    const phone = value || "";
    setPhoneNumber(phone);
    setPhone(phone);
    if (phoneTouched) {
      validatePhoneNumber(phone);
    }
  };

  const validatePhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber || !isPhoneValid(phoneNumber)) {
      setPhoneNumberError("Please enter a valid phone number.");
    } else {
      setPhoneNumberError("");
    }
  };

  const handlePhoneBlur = () => {
    setPhoneTouched(true);
    validatePhoneNumber(phoneNumber);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setEmail(email);
    validateEmail(email);
  };

  const validateEmail = (email: string) => {
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address.");
    } else {
      setEmailError("");
    }
  };

  return (
    <div className="businessUser-form-section">
      <form onSubmit={handleSubmit} className="container">
        {error && <div className="alert alert-danger">{error}</div>}
        <div className="form-group">
          <label className="form-label">Company Name</label>
          <input
            type="text"
            className="form-control"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Name</label>
          <input
            type="text"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            className={`form-control ${emailError ? "error" : ""}`}
            value={email}
            onChange={handleEmailChange}
            required
          />
          {emailError && <div className="text-danger">{emailError}</div>}
        </div>
    
        <div className="form-group">
          <label htmlFor="phoneNumber">Phone:</label>
          <PhoneInput
            style={phoneNumberError ? { border: "1px solid red" } : {}}
            name="phoneNumber"
            value={phone}
            onChange={handlePhoneNumberChange}
            onBlur={handlePhoneBlur}
            defaultCountry={defaultCountry as any}
            className={`custom-phone-input ${phoneNumberError ? "error" : ""}`}
          />
          {phoneNumberError && (
            <div className="text-danger">{phoneNumberError}</div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Address</label>
          <input
            type="text"
            className="form-control"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Address 2</label>
          <input
            type="text"
            className="form-control"
            value={address2}
            onChange={(e) => setAddress2(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">City</label>
          <input
            type="text"
            className="form-control"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Zip Code</label>
          <input
            type="text"
            className="form-control"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Country</label>
          <input
            type="text"
            className="form-control"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Comments</label>
          <textarea
            className="form-control"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </div>
        <div className="button-group">
          <button className="btn btn-outline-success" type="submit">
            {isEditing ? "Update" : "Add"} Business User
          </button>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessUserForm;
